function _typeof(o) {
  "@babel/helpers - typeof";

  return _typeof = "function" == typeof Symbol && "symbol" == typeof Symbol.iterator ? function (o) {
    return typeof o;
  } : function (o) {
    return o && "function" == typeof Symbol && o.constructor === Symbol && o !== Symbol.prototype ? "symbol" : typeof o;
  }, _typeof(o);
}
/**
 * Purpose: SETARRAY opcode — replace a context location with a deep-cloned array.
 *
 * Language shape:
 *   script.destination  — context.* path to overwrite
 *   script.source       — array (resolve_ref or tx collection ref)
 *
 * Special unresolved sources: tx.from / tx.to / tx.path / tx.from.p2sh / tx.to.p2sh
 * resolve to the corresponding collections (not their lengths).
 *
 * Read-only destinations (script.*, witness.*, tx.*, blk.*) are rejected.
 */

module.exports = {
  name: 'SETARRAY',
  description: "\nReplaces a writable context location with a deep clone of a source array.\n\ndestination must be a context.* path. source is resolved via normal RustScript\nreferences, or as a transaction collection (tx.from, tx.to, tx.path,\ntx.from.p2sh, tx.to.p2sh) when unresolved.\n\nThe source is not modified.\n",
  exampleScript: {
    op: 'SETARRAY',
    destination: 'context.successors',
    source: '__opcodes.importarray.successors'
  },
  execute: function execute(node, context) {
    if (!node || _typeof(node) !== 'object' || !context || _typeof(context) !== 'object') {
      return false;
    }
    var destination = node.destination;
    if (typeof destination !== 'string' || destination.length === 0) {
      return false;
    }
    if (!Object.prototype.hasOwnProperty.call(node, 'source')) {
      return false;
    }
    if (!destination.startsWith('context.')) {
      return false;
    }
    var path = destination.slice('context.'.length);
    if (!path || isForbiddenWritePath(path)) {
      return false;
    }
    var sourceArray = resolveSourceArray(node.source, context);
    if (!Array.isArray(sourceArray)) {
      return false;
    }
    return setContextPath(context, path, deepClone(sourceArray));
  }
};
function isForbiddenWritePath(path) {
  return path === 'script' || path.startsWith('script.') || path === 'witness' || path.startsWith('witness.') || path === 'tx' || path.startsWith('tx.') || path === 'blk' || path.startsWith('blk.');
}
function pathSegments(path) {
  return String(path).replace(/\[/g, '.').replace(/\]/g, '').split('.').filter(function (s) {
    return s.length > 0;
  });
}
function setContextPath(root, path, value) {
  var segments = pathSegments(path);
  if (!segments.length) {
    return false;
  }
  var current = root;
  for (var i = 0; i < segments.length; i += 1) {
    var segment = segments[i];
    var isLast = i === segments.length - 1;
    var asIndex = /^[0-9]+$/.test(segment) ? Number(segment) : null;
    if (isLast) {
      if (asIndex !== null) {
        if (!Array.isArray(current) || asIndex >= current.length) {
          return false;
        }
        current[asIndex] = value;
        return true;
      }
      if (!current || _typeof(current) !== 'object' || Array.isArray(current)) {
        return false;
      }
      current[segment] = value;
      return true;
    }
    if (asIndex !== null) {
      if (!Array.isArray(current) || asIndex >= current.length) {
        return false;
      }
      current = current[asIndex];
      continue;
    }
    if (!current || _typeof(current) !== 'object' || Array.isArray(current)) {
      return false;
    }
    if (!Object.prototype.hasOwnProperty.call(current, segment)) {
      current[segment] = {};
    }
    current = current[segment];
  }
  return false;
}
function isP2shSlip(slip) {
  if (!slip || _typeof(slip) !== 'object') {
    return false;
  }
  if (slip.slip_type === 'Bound' || slip.slip_type === 9) {
    return false;
  }
  var pk = slip.public_key || slip.publicKey;
  if (Array.isArray(pk) && pk[0] === 0) {
    return true;
  }
  if (pk && _typeof(pk) === 'object' && pk[0] === 0) {
    return true;
  }
  return false;
}
function resolveSourceArray(source, context) {
  var resolved = resolveRef(context, source);
  if (Array.isArray(resolved)) {
    return resolved;
  }
  if (typeof source !== 'string') {
    return null;
  }
  if (!context.tx || _typeof(context.tx) !== 'object') {
    if (source === 'tx.from' || source === 'tx.to' || source === 'tx.path' || source === 'tx.from.p2sh' || source === 'tx.to.p2sh') {
      return null;
    }
  }
  if (source === 'tx.from') {
    return Array.isArray(context.tx.from) ? context.tx.from : null;
  }
  if (source === 'tx.to') {
    return Array.isArray(context.tx.to) ? context.tx.to : null;
  }
  if (source === 'tx.path') {
    return Array.isArray(context.tx.path) ? context.tx.path : null;
  }
  if (source === 'tx.from.p2sh') {
    return Array.isArray(context.tx.from) ? context.tx.from.filter(isP2shSlip) : null;
  }
  if (source === 'tx.to.p2sh') {
    return Array.isArray(context.tx.to) ? context.tx.to.filter(isP2shSlip) : null;
  }
  return null;
}
function deepClone(value) {
  return JSON.parse(JSON.stringify(value));
}
function resolveRef(root, ref) {
  if (typeof ref !== 'string') {
    return ref;
  }
  var parts = ref.split('.');
  var cursor = root;
  for (var i = 0; i < parts.length; i += 1) {
    var key = parts[i];
    if (!cursor || _typeof(cursor) !== 'object' || !Object.prototype.hasOwnProperty.call(cursor, key)) {
      return ref;
    }
    cursor = cursor[key];
  }
  return cursor;
}